import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { ReactComponent as ProfileIcon } from "../../icons/profile icon.svg";
import { ReactComponent as SearchIcon } from "../../icons/search icon.svg";
import { ReactComponent as CartIcon } from "../../icons/cart icon.svg";
import { ReactComponent as CallIcon } from "../../icons/call icon.svg";
import { ReactComponent as MailIcon } from "../../icons/mail icon.svg";
import googlePlayBadge from "../../icons/Google Play.svg";
import appStoreBadge from "../../icons/App store.svg";
import { resetCart } from "../../features/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../features/authSlice";
import { persistor } from "../../app/store";
import { resetSubscription } from "../../features/subscriptionSlice";
import { clearWishlistLocal } from "../../features/wishlistSlice";
import LoginPromptModal from "../../components/home/authComponents/LoginPromptModal";
import { jwtDecode } from "jwt-decode";

const Header = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const cartItems = useSelector((state) => state.cart.items) || [];
  const wishlistItems = useSelector((state) => state.wishlist.items) || [];

  const cartCount = cartItems.length;
  const wishlistCount = wishlistItems.length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const { exp } = jwtDecode(token);
        const isExpired = Date.now() >= exp * 1000;

        if (isExpired) {
          dispatch(resetCart());
          dispatch(resetSubscription());
          dispatch(clearWishlistLocal());
          dispatch(logout());
          persistor.purge();
          localStorage.removeItem("authToken");
          navigate("/login");
        }
      } catch (err) {
        console.error("Token decode error:", err);
        dispatch(logout());
        localStorage.removeItem("authToken");
        navigate("/login");
      }
    }
  }, [dispatch, navigate]);


  const handleLogout = () => {
    persistor.purge();
    dispatch(resetCart());
    dispatch(resetSubscription());
    dispatch(clearWishlistLocal());
    dispatch(logout());
    navigate("/login");
  };

  return (
    <header className="bg-primary text-white text-sm sticky top-0 z-[9999]">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col gap-2 sm:flex-row items-center justify-between">
        {/* Contact Info */}
        <div className="hidden sm:flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
          <div className="flex items-center gap-1">
            <CallIcon className="h-4 w-4" />
            <a href="tel:+918600040039" className="text-white hover:text-gray">
              +91 8600040039
            </a>
          </div>
          <span className="hidden sm:block">|</span>
          <div className="flex items-center gap-1">
            <MailIcon className="h-5 w-5" />
            <a
              href="mailto:care@booksatdoorsteps.com?subject=Customer%20Service%20Inquiry"
              className="leading-none"
            >
              care@booksatdoorsteps.com
            </a>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex flex-wrap items-center justify-center sm:justify-end gap-5 sm:gap-6 text-center sm:text-right">
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline">Download Our App</span>

            <a
              href="https://play.google.com/store/apps/details?id=com.applauseitsolutions.booksatdoorsteps"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Google Play"
                className="h-7 object-contain"
              />
            </a>

            <a
              href="https://apps.apple.com/in/app/booksatdoorsteps/id6478926787"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={appStoreBadge}
                alt="App Store"
                className="h-7 object-contain"
              />
            </a>
          </div>

          <span className="hidden sm:block">|</span>

          <div
            className="flex items-center gap-3 relative z-50"
            ref={dropdownRef}
          >
            <ProfileIcon
              className="h-5 w-5 cursor-pointer hover:opacity-80"
              onClick={() => setShowDropdown((prev) => !prev)}
            />

            {showDropdown && (
              <div className="absolute right-0 top-8 w-44 bg-white text-gray-800 rounded-xl shadow-xl z-50 border border-gray-200 animate-fade-in">
                {!isAuthenticated ? (
                  <Link
                    to="/login"
                    className="block text-left px-5 py-3 text-sm font-medium rounded-t-xl hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                    onClick={() => setShowDropdown(false)}
                  >
                    Login
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/profile"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      My Profile
                    </Link>

                    <Link
                      to="/myOrders"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      My Orders
                    </Link>

                    <Link
                      to="/subscription-details"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      Subscriptions
                    </Link>

                    <Link
                      to="/books-with-me"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      Books With Me
                    </Link>

                    <div className="border-t border-gray-100 my-1 mx-2" />

                    <button
                      onClick={() => {
                        handleLogout();
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-5 py-3 text-sm font-medium rounded-b-xl hover:bg-gray-50 hover:text-red-500 transition-all duration-200"
                    >
                      Logout
                    </button>
                  </>
                )}
              </div>
            )}

            <Link to="/search">
              <SearchIcon className="h-6 w-6 cursor-pointer hover:opacity-80" />
            </Link>

            <button
              onClick={() =>
                isAuthenticated
                  ? navigate("/wishlist")
                  : setShowLoginModal(true)
              }
              className="relative"
            >
              <Heart className="h-5 w-5 cursor-pointer hover:opacity-80" />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            <button
              onClick={() =>
                isAuthenticated ? navigate("/cart") : setShowLoginModal(true)
              }
              className="relative"
            >
              <CartIcon className="h-5 w-5 cursor-pointer hover:opacity-80" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </header>
  );
};

export default Header;
